import { capitalizeString, formatWorkOrderNumber } from '@zspace/format';
import { FeaturePermissions, WorkOrderPermissions } from '@zspace/roles';
import {
  DeferredResponse,
  WorkOrderDetailsData,
  WorkOrderStatus,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import {
  FaBuilding,
  FaCalendar,
  FaDownload,
  FaGears,
  FaLaptop,
  FaRotateLeft,
  FaUser,
} from 'react-icons/fa6';
import {
  Link,
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
} from 'react-router-dom';
import DeviceAssignmentsTable from '../../device-groups/device-assignments-table/device-assignments-table';
import BackButton from '../../shared/back-button/back-button';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import { useCSVDownload } from '../../shared/hooks/csv-download';
import useHttpRequest from '../../shared/hooks/http-request';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { formatDateToLocaleString } from '../../shared/utils';
import BoxLayout from '../../ui/box-layout/box-layout';
import { Button } from '../../ui/button/button';
import DetailsCard from '../../ui/details-card/details-card';
import InfoLabel from '../../ui/info-label/info-label';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import ViewAllList from '../../ui/view-all-list/view-all-list';
import WorkOrderConfirmStatusUpdateModal from '../work-order-confirm-status-update-modal/work-order-confirm-status-update-modal';
import {
  fetchWorkOrderDetails,
  fetchWorkOrderDetailsDownloadableData,
  sendWorkOrderToDrafts,
} from '../work-orders-service';

const SEND_WORK_ORDER_TO_DRAFTS_ERROR_MESSAGE =
  'The work order could not be sent to drafts. Please try again';
const EXPORT_WORK_ORDER_ERROR_MESSAGE =
  'The work order could not be exported. Please try again';

export const loader: LoaderFunction = async ({ params }) => {
  const workOrderId = params.id;
  const response = fetchWorkOrderDetails(workOrderId as string);

  return defer({ response });
};

export function WorkOrderDetailsPageContent() {
  const navigate = useNavigate();
  const {
    executeHttpRequest: executeSendWorkOrderToDraftsRequest,
    isLoading: isLoadingSendToDraft,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeExportWorkOrderRequest,
    isLoading: isLoadingExportWorkOrderCsv,
  } = useHttpRequest();
  const workOrderDetailsData = useAsyncValue() as WorkOrderDetailsData;
  const [showModal, setShowModal] = useState<boolean>(false);
  const { downloadCSV } = useCSVDownload();

  const title = useMemo(() => {
    const workOrder = workOrderDetailsData.workOrder;
    return `${formatWorkOrderNumber(workOrder.number)} - ${
      workOrder.name
    } details`;
  }, [workOrderDetailsData]);

  const accounts = useMemo(() => {
    const accountsList = [
      ...new Map(
        workOrderDetailsData.workOrder.salesOrders.map((so) => [
          so.opportunity?.account?.id,
          so.opportunity?.account,
        ])
      ).values(),
    ];

    return (
      <ViewAllList
        list={accountsList}
        horizontalList
        renderItem={(account) => (
          <Link to={`/accounts/${account?.id}`}>{account?.name}</Link>
        )}
        separator=", "
      />
    );
  }, [workOrderDetailsData]);

  const canBeSentBackToDrafts = useMemo(() => {
    const workOrder = workOrderDetailsData.workOrder;
    return (
      workOrder.currentStatus.status === WorkOrderStatus.QUEUE &&
      !!workOrder.priority &&
      workOrder.priority > 0
    );
  }, [workOrderDetailsData.workOrder]);

  const canBeExported = useMemo(() => {
    const workOrderStatus = workOrderDetailsData.workOrder.currentStatus.status;
    return (
      workOrderStatus === WorkOrderStatus.QUEUE ||
      workOrderStatus === WorkOrderStatus.COMPLETED
    );
  }, [workOrderDetailsData.workOrder]);

  const handleSendToDraftsClick = useCallback(
    async () =>
      executeSendWorkOrderToDraftsRequest({
        asyncFunction: async () => {
          await sendWorkOrderToDrafts(workOrderDetailsData.workOrder.id);
          navigate('/work-orders/dashboard/drafts');
        },
        customErrorMessage: SEND_WORK_ORDER_TO_DRAFTS_ERROR_MESSAGE,
      }),
    [
      executeSendWorkOrderToDraftsRequest,
      navigate,
      workOrderDetailsData.workOrder.id,
    ]
  );

  const handleExportWorkOrderClick = useCallback(
    async () =>
      executeExportWorkOrderRequest({
        asyncFunction: async () => {
          const workOrderDetailsDownloadableData =
            await fetchWorkOrderDetailsDownloadableData(
              workOrderDetailsData.workOrder.id
            );
          downloadCSV(workOrderDetailsDownloadableData);
        },
        customErrorMessage: EXPORT_WORK_ORDER_ERROR_MESSAGE,
      }),
    [
      downloadCSV,
      executeExportWorkOrderRequest,
      workOrderDetailsData.workOrder.id,
    ]
  );

  return (
    <>
      <BoxLayout
        className="is-min-height-80 mx-10 my-4"
        header={
          <Columns marginless display="flex">
            <Columns.Column>
              <BackButton />
              <h1 className="is-size-3 has-text-weight-light">{title}</h1>
            </Columns.Column>
            <Columns.Column
              display="flex"
              className="gap-3"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <If condition={canBeSentBackToDrafts}>
                <CheckPermissions
                  permissions={
                    WorkOrderPermissions.WORK_ORDERS_QUEUE_PRIORITIZATION
                  }
                >
                  <CheckPermissions.Render>
                    <Button
                      className="is-borderless hoverable-primary-dark"
                      color="transparent"
                      onClick={() => setShowModal(true)}
                    >
                      <Icon color="primary-dark">
                        <FaRotateLeft />
                      </Icon>
                      <span className="is-underlined has-text-primary-dark">
                        Send back to drafts
                      </span>
                    </Button>
                  </CheckPermissions.Render>
                </CheckPermissions>
              </If>
              <If condition={canBeExported}>
                <CheckPermissions
                  permissions={WorkOrderPermissions.WORK_ORDERS_DETAILS_EXPORT}
                >
                  <CheckPermissions.Render>
                    <Button
                      color="primary-dark"
                      isExecutingAction={isLoadingExportWorkOrderCsv}
                      onClick={handleExportWorkOrderClick}
                    >
                      <Button.LoadingIcon icon={FaDownload} />
                      <span>Export</span>
                    </Button>
                  </CheckPermissions.Render>
                </CheckPermissions>
              </If>
            </Columns.Column>
          </Columns>
        }
      >
        <Columns className="p-4">
          <Columns.Column size="one-fifth">
            <Element>
              <DetailsCard title="Basic Information">
                <InfoLabel
                  icon={<FaBuilding />}
                  label="Accounts"
                  content={accounts}
                />
                <InfoLabel
                  icon={<FaLaptop />}
                  label="Devices"
                  content={workOrderDetailsData.devices.length.toString()}
                />
                <InfoLabel
                  icon={<FaUser />}
                  label="Created by"
                  content={workOrderDetailsData.workOrder.createdBy}
                />
                <InfoLabel
                  icon={<FaGears />}
                  label="Status"
                  content={capitalizeString(
                    workOrderDetailsData.workOrder.currentStatus.status
                  )}
                />
                <InfoLabel
                  icon={<FaCalendar />}
                  label="Creation date"
                  content={formatDateToLocaleString(
                    workOrderDetailsData.workOrder.createdAt
                  )}
                />
                <If
                  condition={
                    workOrderDetailsData.workOrder.currentStatus.status ===
                    WorkOrderStatus.QUEUE
                  }
                >
                  <InfoLabel
                    icon={<FaCalendar />}
                    label="Priority"
                    content={workOrderDetailsData.workOrder.priority}
                  />
                </If>
                <If
                  condition={
                    workOrderDetailsData.workOrder.currentStatus.status ===
                    WorkOrderStatus.COMPLETED
                  }
                >
                  <InfoLabel
                    icon={<FaCalendar />}
                    label="Completed by Genesis date"
                    content={formatDateToLocaleString(
                      workOrderDetailsData.workOrder.currentStatus.createdAt
                    )}
                  />
                </If>
              </DetailsCard>
            </Element>
          </Columns.Column>
          <Columns.Column size="four-fifths">
            <DeviceAssignmentsTable
              data={workOrderDetailsData.devices}
              assignmentsRowTitle={
                workOrderDetailsData.workOrder.currentStatus.status ===
                WorkOrderStatus.COMPLETED
                  ? 'Original software configuration'
                  : 'Assigned software'
              }
            />
          </Columns.Column>
        </Columns>
      </BoxLayout>
      <WorkOrderConfirmStatusUpdateModal
        show={showModal}
        targetStatus={WorkOrderStatus.DRAFT}
        onCancel={() => setShowModal(false)}
        onConfirm={handleSendToDraftsClick}
        loading={isLoadingSendToDraft}
      />
    </>
  );
}

export function WorkOrderDetailsPage() {
  const { response } =
    useLoaderData() as DeferredResponse<WorkOrderDetailsData>;

  return (
    <ProtectedPage
      permissions={[
        FeaturePermissions.FULL_CONFIGURATION_ACCESS,
        WorkOrderPermissions.WORK_ORDERS_QUEUE_READ,
        WorkOrderPermissions.WORK_ORDERS_COMPLETED_READ,
      ]}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <WorkOrderDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default WorkOrderDetailsPage;
